/**
 * Left column: traffic AI (default paths), rule groups, default path list.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo } from 'react';
import type {
  VoluumFlowUpdate,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

import { DefaultPathListRow } from './default-paths';
import { PATH_LIST_CONTAINER_CLASS } from '../../paths/path-list-chrome';
import {
  usePreviewMode,
  usePreviewRowLookup,
} from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import {
  PreviewSortLabel,
  usePreviewMetricSort,
} from '@/features/flow-optimizer/components/preview/sort';
import {
  defaultPathSelection,
  isDefaultPathSelected,
  type PathsSelection,
} from './selection';
import {
  DefaultPathsOptimizationRow,
  TrafficDistributionAiPathsCard,
} from './flow-controls';
import RuleGroupsSection from './RuleGroupsSection';
import { getSetAutoOptimized } from '../../utils/set-automation';
import { useFlowTrackerName, useTrackerAi } from '@/features/flow-optimizer/tracker';

export interface PathsLeftColumnProps {
  draft: VoluumFlowUpdate;
  onPatch: (mutator: (d: VoluumFlowUpdate) => void) => void;
  selection: PathsSelection;
  onSelect: (s: PathsSelection) => void;
  percents: number[];
}

const PathsLeftColumn: FC<PathsLeftColumnProps> = ({
  draft,
  onPatch,
  selection,
  onSelect,
  percents,
}) => {
  const previewMode = usePreviewMode();
  const lookup = usePreviewRowLookup();
  const { sort, cycle, sortEntries } = usePreviewMetricSort();
  const paths = draft.defaultPaths ?? [];
  // A tracker without its own AI ignores these flags — never lock on them.
  const trackerAi = useTrackerAi();
  const weightLocked = trackerAi && getSetAutoOptimized(draft);
  const trackerName = useFlowTrackerName();
  const pathEntries = useMemo(() => {
    const base = paths.map((path, index) => ({ path, index }));
    if (!previewMode) return base;
    return sortEntries(base, ({ path }) => ({
      active: path.active !== false,
      name: path.name ?? '',
      row: lookup('paths', path.id ?? ''),
    }));
  }, [paths, previewMode, sortEntries, lookup]);

  const patchPath = (idx: number, mutator: (p: VoluumPathUpdate) => void) =>
    onPatch((d) => {
      const t = d.defaultPaths?.[idx];
      if (t) mutator(t);
    });

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-2 overflow-y-auto pr-0 lg:basis-[30%] lg:flex-none lg:pr-2">
      {!previewMode && trackerAi ? (
        <TrafficDistributionAiPathsCard draft={draft} onPatch={onPatch} />
      ) : null}

      <RuleGroupsSection
        draft={draft}
        onPatch={onPatch}
        selection={selection}
        onSelect={onSelect}
      />

      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="text-[10px] font-semibold uppercase tracking-wide text-foreground">
            Default paths
          </h4>
          <span className="text-[10px] tabular-nums text-muted-foreground">
            {paths.length}
          </span>
        </div>
        <DefaultPathsOptimizationRow
          draft={draft}
          onPatch={onPatch}
          hideWeightOptimization={previewMode || !trackerAi}
        />
        {paths.length === 0 ? (
          <p className="text-[11px] italic text-muted-foreground">
            {trackerName} requires at least one default path.
          </p>
        ) : (
          <>
            {previewMode ? (
              // Column labels for the preview cells. Grid MUST match the
              // preview gridCols in DefaultPathListRow.
              <div className="grid w-full items-center gap-x-1.5 px-1.5 grid-cols-[minmax(0,1fr)_3rem_0.75rem_3rem_3.25rem_2.75rem]">
                <span />
                <PreviewSortLabel column="current" label="Current" sort={sort} onCycle={cycle} />
                <span />
                <PreviewSortLabel column="new" label="New" sort={sort} onCycle={cycle} />
                <PreviewSortLabel column="diff" label="Diff" sort={sort} onCycle={cycle} />
                <span />
              </div>
            ) : null}
            <div
              className={PATH_LIST_CONTAINER_CLASS}
              role="listbox"
              aria-label="Default paths"
            >
            {pathEntries.map(({ path: p, index: idx }) => (
              <DefaultPathListRow
                key={p.id ?? `path-${idx}`}
                path={p}
                selected={isDefaultPathSelected(selection, idx)}
                weightPercent={percents[idx] ?? 0}
                weightLocked={weightLocked}
                onSelect={() => onSelect(defaultPathSelection(idx))}
                onPatch={(mutator) => patchPath(idx, mutator)}
              />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PathsLeftColumn;
